<template>
  <div>
    <assessment-helper></assessment-helper>

    <div class="activity-stage a-letters-kpm">
      <div class="letter-wrapper">
        <svg height="134px" width="134px" v-html="getKeySvg()" />
      </div>
    </div>

    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax } from 'gsap';
import ActivityManager from 'mixins/ActivityManager';
import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import AssessmentHelper from 'components/utils/AssessmentHelper.vue';

// row 1
import KeyQ from 'assets/svg/keyboards/k2-keys-lite/KeyQ.svg';
import KeyW from 'assets/svg/keyboards/k2-keys-lite/KeyW.svg';
import KeyE from 'assets/svg/keyboards/k2-keys-lite/KeyE.svg';
import KeyR from 'assets/svg/keyboards/k2-keys-lite/KeyR.svg';
import KeyT from 'assets/svg/keyboards/k2-keys-lite/KeyT.svg';
import KeyY from 'assets/svg/keyboards/k2-keys-lite/KeyY.svg';
import KeyU from 'assets/svg/keyboards/k2-keys-lite/KeyU.svg';
import KeyI from 'assets/svg/keyboards/k2-keys-lite/KeyI.svg';
import KeyO from 'assets/svg/keyboards/k2-keys-lite/KeyO.svg';
import KeyP from 'assets/svg/keyboards/k2-keys-lite/KeyP.svg';
import KeyBracket1 from 'assets/svg/keyboards/k2-keys-lite/KeyBracket1.svg';
import KeyBracket2 from 'assets/svg/keyboards/k2-keys-lite/KeyBracket2.svg';
import KeyBSlash from 'assets/svg/keyboards/k2-keys-lite/KeyBSlash.svg';

// // row 2
import KeyA from 'assets/svg/keyboards/k2-keys/KeyAHR.svg';
import KeyS from 'assets/svg/keyboards/k2-keys/KeySHR.svg';
import KeyD from 'assets/svg/keyboards/k2-keys/KeyDHR.svg';
import KeyF from 'assets/svg/keyboards/k2-keys/KeyFHR.svg';
import KeyG from 'assets/svg/keyboards/k2-keys-lite/KeyG.svg';
import KeyH from 'assets/svg/keyboards/k2-keys-lite/KeyH.svg';
import KeyJ from 'assets/svg/keyboards/k2-keys/KeyJHR.svg';
import KeyK from 'assets/svg/keyboards/k2-keys/KeyKHR.svg';
import KeyL from 'assets/svg/keyboards/k2-keys/KeyLHR.svg';
import KeySemicolon from 'assets/svg/keyboards/k2-keys/KeySemicolonHR.svg';
import KeyApost from 'assets/svg/keyboards/k2-keys-lite/KeyApost.svg';

// // row 3
import KeyZ from 'assets/svg/keyboards/k2-keys-lite/KeyZ.svg';
import KeyX from 'assets/svg/keyboards/k2-keys-lite/KeyX.svg';
import KeyC from 'assets/svg/keyboards/k2-keys-lite/KeyC.svg';
import KeyV from 'assets/svg/keyboards/k2-keys-lite/KeyV.svg';
import KeyB from 'assets/svg/keyboards/k2-keys-lite/KeyB.svg';
import KeyN from 'assets/svg/keyboards/k2-keys-lite/KeyN.svg';
import KeyM from 'assets/svg/keyboards/k2-keys-lite/KeyM.svg';
import KeyComma from 'assets/svg/keyboards/k2-keys-lite/KeyComma.svg';
import KeyPeriod from 'assets/svg/keyboards/k2-keys-lite/KeyPeriod.svg';
import KeyFSlash from 'assets/svg/keyboards/k2-keys-lite/KeyFSlash.svg';

export default {
  components: {
    AssessmentHelper
  },

  mixins: [ActivityManager, AssetLoader, ErrorLogger, TypingActivityHelper],

  data() {
    return {
      activityName: 'ALettersKPM',
      keyboardData: {
        // row1
        KeyQ,
        KeyW,
        KeyE,
        KeyR,
        KeyT,
        KeyY,
        KeyU,
        KeyI,
        KeyO,
        KeyP,
        KeyBracket1,
        KeyBracket2,
        KeyBSlash,

        // row2
        KeyA,
        KeyS,
        KeyD,
        KeyF,
        KeyG,
        KeyH,
        KeyJ,
        KeyK,
        KeyL,
        KeySemicolon,
        KeyApost,

        // row3
        KeyZ,
        KeyX,
        KeyC,
        KeyV,
        KeyB,
        KeyN,
        KeyM,
        KeyComma,
        KeyPeriod,
        KeyFSlash
      }
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
    },

    getActivityState() {
      const { rounds, keyboard } = this.activityData;
      const { characters } = keyboard || {};

      const isTouchHold =
        Boolean(characters) && characters.includes('F') !== characters.includes('J');

      const written = isTouchHold
        ? 'Hold the target. Then type the letters at the top.'
        : 'Type the letters at the top.';
      const voice = isTouchHold
        ? `${this.voiceInstructionsPrefix}/spotchecklettersa-${this.getRandomVoice()}.mp3`
        : ` ${this.voiceInstructionsPrefix}/paintedfingercluesnth-${this.getRandomVoice()}-1.mp3`;

      return {
        rounds,
        instructions: {
          written,
          voice,
          delay: 4
        }
      };
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard || {
        keyboardType: undefined,
        roofs: undefined
      };

      return {
        keyboardType: keyboardType || 'KeyboardK2',
        roofs
      };
    },

    startActivity() {
      this.setupNewRound();
      this.errorLoggerStart();
      this.$nextTick(() => {
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
        TweenMax.to('.letter-wrapper', 0.75, {
          opacity: 1,
          onComplete: this.enableKeyboard
        });
      });
    },

    handleCorrectKey() {
      this.disableKeyboard();
      this.sndType();

      TweenMax.to('.letter-wrapper', 0.2, {
        opacity: 0,
        onComplete: this.finishStep
      });
    },

    finishStep() {
      if (this.isLastStepInRound) {
        this.sndBell();
        this.hideKeyboard();
        TweenMax.delayedCall(0.5, this.handleRoundEnd);
      } else {
        this.completeStep();
        this.setCurrentStep();
        TweenMax.to('.letter-wrapper', 0.2, {
          opacity: 1,
          onComplete: this.enableKeyboard
        });
      }
    },

    handleRoundEnd() {
      this.completeActivity();
    },

    getLettersKeyCode(letter) {
      switch (letter) {
        case 'Semicolon':
        case ';':
          return 'KeySemicolon';
        case 'Bracket1':
        case '[':
          return 'KeyBracket1';
        case 'Bracket2':
        case ']':
          return 'KeyBracket2';
        case 'BSlash':
        case '\\':
          return 'KeyBSlash';
        case 'Apost':
        case "'":
          return 'KeyApost';
        case 'Comma':
        case ',':
          return 'KeyComma';
        case 'Period':
        case '.':
          return 'KeyPeriod';
        case 'FSlash':
        case '/':
          return 'KeyFSlash';

        default:
          return `Key${letter}`;
      }
    },

    getKeySvg() {
      if (!this.currentStep.answer) {
        return null;
      }

      const key = this.getLettersKeyCode(this.currentStep.answer.toUpperCase());
      return this.keyboardData[key];
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';
</style>
